import { AssetVizMode } from '@asset-viz/defines'
import { message } from 'antd'
import { GeckoColorScale } from 'color-scale-kit'
import { Accordion, type AccordionItem } from 'components/Accordion'
import CantileverButton from 'components/CantileverButton'
import { ForecastAssetViz } from 'features/assetVizAdapter'
import { GenericRangeSelector } from 'features/dataFilter'
import {
  determineDefaultColorScaleThreshold,
  useColorScale,
  useQuantitativeDataSummaryStats,
} from 'features/dataLayers'
import { DEFAULT_SCENE_CONFIG } from 'features/richText/hooks/useSlashCommandAssetVizData'
import {
  createColorThresholdFromParsed,
  createMeasurementRangeFromParsed,
  parseColorThresholdsFromMetadata,
  parseFilterRangesFromMetadata,
  parseSelectedCdfIdsFromMetadata,
  serializeColorThresholds,
  serializeFilterRanges,
} from 'features/richText/utils/unitConfigMetadataUtils'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useGeckoTheme } from 'styles/GeckoTheme'
import {
  GeckoAssetModel,
  GeckoColorScaleThreshold,
  GeckoComponent,
  GeckoComponentDataFrame,
  GeckoComponentDefaults,
  GeckoMeasurementRange,
} from 'types'

import {
  ButtonGroup,
  Label,
  Message,
  ModalFooter,
  Section,
  Select,
} from './shared/SharedStyledComponents'
import { SlashCommandModal, SlashCommandModalHeader } from './sharedStyles'

const ModalContentWrapper = styled.div`
  max-width: 90vw;
  max-height: 90vh;
  width: 80rem;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.forecast.cardGroupBackground};
  border-radius: 0.5rem;
  padding: 1.5rem;
  border: 1px solid ${({ theme }) => theme.forecast.border};
`

const ModalBodyWrapper = styled.div`
  flex: 1;
  min-height: 0;
  margin-bottom: 1.5rem;
`

const ContentLayout = styled.div`
  display: flex;
  gap: 1rem;
  height: 100%;
  min-height: 0;
`

const VizContainer = styled.div`
  height: 40rem;
  flex: 1;
  min-height: 0;
  background: ${({ theme }) => theme.forecast.cardBackground};
  border-radius: 0.25rem;
`

const ControlsPanel = styled.div`
  width: 22rem;
  overflow-y: auto;
  background: ${({ theme }) => theme.forecast.cardBackground};
  border-radius: 0.25rem;
  padding: 1rem;
`

interface ComponentFilterInputProps {
  cdf: GeckoComponentDataFrame
  componentDefaults_: GeckoComponentDefaults | undefined
  colorThreshold: GeckoColorScaleThreshold | undefined
  filteredValueRange: GeckoMeasurementRange | undefined
  onColorThresholdChange: (threshold: GeckoColorScaleThreshold) => void
  onFilteredValueRangeChange: (range: GeckoMeasurementRange | undefined) => void
  onCategoricalValuesSelected: (values: number[] | undefined) => void
}

const ComponentFilterInput: React.FC<ComponentFilterInputProps> = ({
  cdf,
  componentDefaults_,
  colorThreshold,
  filteredValueRange,
  onColorThresholdChange,
  onFilteredValueRangeChange,
}) => {
  const cdfStats = useQuantitativeDataSummaryStats(cdf)

  const defaultColorThreshold = useMemo(
    () =>
      colorThreshold ||
      determineDefaultColorScaleThreshold(
        cdf.data.params.view,
        cdf.data.params.measurement_unit,
        cdfStats.data
      ),
    [colorThreshold, cdf, cdfStats.data]
  )

  const { colorScale } = useColorScale(cdf, defaultColorThreshold)

  const { minThresholdColor, midThresholdColor, maxThresholdColor } =
    useMemo(() => {
      const colorcetColorScale = new GeckoColorScale({
        colorcetId: cdf.data.params.view.colorcet.colorcetId,
        valuesToColorRange: [
          [0, cdf.data.params.view.colorcet.start],
          [0.5, cdf.data.params.view.colorcet.middle],
          [1.0, cdf.data.params.view.colorcet.end],
        ],
      })
      return {
        minThresholdColor: colorcetColorScale.getColorForValue(0.0),
        midThresholdColor: colorcetColorScale.getColorForValue(0.5),
        maxThresholdColor: colorcetColorScale.getColorForValue(1.0),
      }
    }, [cdf])

  if (!defaultColorThreshold) {
    return <Message>Loading color scale...</Message>
  }

  if (!cdfStats.data?.value_range) {
    return <Message>Loading data statistics...</Message>
  }

  return (
    <GenericRangeSelector
      cdfId={cdf.id}
      selectedComponentDefaults={componentDefaults_}
      measurementUnit={cdf.data.params.measurement_unit}
      selectedColorScale={colorScale}
      filteredValueRange={filteredValueRange}
      onFilteredValueRangeChange={onFilteredValueRangeChange}
      fullValueRange={cdfStats.data.value_range}
      cdfStatsHistogram={cdfStats.data?.histogram}
      onColorThresholdChanged={onColorThresholdChange}
      selectedColorThreshold={defaultColorThreshold}
      minThresholdColor={minThresholdColor}
      midThresholdColor={midThresholdColor}
      maxThresholdColor={maxThresholdColor}
    />
  )
}

interface UnitConfigurationModalProps {
  entryName: string
  assetModel: GeckoAssetModel | undefined
  components: GeckoComponent[]
  componentDataFrames: GeckoComponentDataFrame[]
  componentDefaults: GeckoComponentDefaults[]
  allComponentDataFrames: GeckoComponentDataFrame[]
  metadata?: Record<string, string | null | undefined>
  onMetadataChange?: (
    metadata: Record<string, string | null | undefined>
  ) => void
  onClose: () => void
  isSingleComponentView?: boolean
}

export const UnitConfigurationModal: React.FC<UnitConfigurationModalProps> = ({
  entryName,
  assetModel,
  components,
  componentDataFrames: initialComponentDataFrames,
  componentDefaults,
  allComponentDataFrames,
  metadata,
  onMetadataChange,
  onClose,
  isSingleComponentView = false,
}) => {
  const { theme } = useGeckoTheme()

  const [filteredRanges, setFilteredRanges] = useState<
    Map<string, GeckoMeasurementRange>
  >(new Map())
  const [categoricalValues, setCategoricalValues] = useState<
    Map<string, number[]>
  >(new Map())
  const [colorThresholds, setColorThresholds] = useState<
    Map<string, GeckoColorScaleThreshold>
  >(new Map())
  const [selectedCdfIds, setSelectedCdfIds] = useState<Map<string, string>>(
    new Map()
  )

  const isMCVConfigured = useMemo(() => {
    return Array.from(assetModel?.data.components?.values() ?? []).some(
      (component) => component.sceneConfig
    )
  }, [assetModel])

  const componentDataFrames = useMemo(() => {
    if (selectedCdfIds.size === 0) {
      return initialComponentDataFrames
    }

    return initialComponentDataFrames.map((cdf) => {
      const componentId = cdf.tags.component
      if (!componentId) return cdf

      const selectedId = selectedCdfIds.get(componentId)
      if (!selectedId) return cdf

      const selectedCdf = allComponentDataFrames.find(
        (c) => c.id === selectedId
      )
      return selectedCdf || cdf
    })
  }, [selectedCdfIds, initialComponentDataFrames, allComponentDataFrames])

  const cdfsByComponent = useMemo(() => {
    const map = new Map<string, GeckoComponentDataFrame[]>()

    allComponentDataFrames.forEach((cdf) => {
      const componentId = cdf.tags.component
      if (!componentId) return

      if (!map.has(componentId)) {
        map.set(componentId, [])
      }
      map.get(componentId)?.push(cdf)
    })

    return map
  }, [allComponentDataFrames])

  const parsedSavedThresholds = useMemo(
    () => parseColorThresholdsFromMetadata(metadata),
    [metadata]
  )

  const parsedSavedRanges = useMemo(
    () => parseFilterRangesFromMetadata(metadata),
    [metadata]
  )

  const parsedSavedCdfIds = useMemo(
    () => parseSelectedCdfIdsFromMetadata(metadata),
    [metadata]
  )

  useEffect(() => {
    setSelectedCdfIds(parsedSavedCdfIds)
    setColorThresholds(new Map())
    setFilteredRanges(new Map())
  }, [parsedSavedCdfIds])

  const handleSaveChanges = useCallback(() => {
    if (!onMetadataChange) return

    onMetadataChange({
      ...metadata,
      colorThresholds: serializeColorThresholds(colorThresholds),
      filterRanges: serializeFilterRanges(filteredRanges),
      selectedCdfIds: JSON.stringify(Object.fromEntries(selectedCdfIds)),
    })

    message.success('Configuration saved successfully')
  }, [
    onMetadataChange,
    colorThresholds,
    filteredRanges,
    selectedCdfIds,
    metadata,
  ])

  const handleResetChanges = useCallback(() => {
    setColorThresholds(new Map())
    setFilteredRanges(new Map())
    setCategoricalValues(new Map())
    setSelectedCdfIds(new Map())
  }, [])

  const getColorThresholdForComponent = useCallback(
    (cdf: GeckoComponentDataFrame) => {
      const componentId = cdf.tags.component
      if (!componentId) return undefined

      const key = `${componentId}:${cdf.data.params.view.id}`

      return (
        colorThresholds.get(key) ??
        (parsedSavedThresholds?.[key]
          ? createColorThresholdFromParsed(parsedSavedThresholds[key])
          : undefined) ??
        componentDefaults
          .find((cd) => cd.tags.component === componentId)
          ?.data.color_scale_thresholds.get(cdf.data.params.view.id)
      )
    },
    [colorThresholds, componentDefaults, parsedSavedThresholds]
  )

  const getFilteredValueRange = useCallback(
    (cdf: GeckoComponentDataFrame) => {
      const componentId = cdf.tags.component
      if (!componentId) return undefined

      const key = `${componentId}:${cdf.data.params.view.id}`

      return (
        filteredRanges.get(key) ??
        (parsedSavedRanges?.[key]
          ? createMeasurementRangeFromParsed(parsedSavedRanges[key])
          : undefined)
      )
    },
    [filteredRanges, parsedSavedRanges]
  )

  const getSelectedCategoricalValues = useCallback(
    (cdf: GeckoComponentDataFrame) => categoricalValues.get(cdf.id),
    [categoricalValues]
  )

  const handleFilteredValueRangeChange = useCallback(
    (key: string, range: GeckoMeasurementRange | undefined) => {
      setFilteredRanges((prev) => {
        const next = new Map(prev)
        if (range) next.set(key, range)
        else next.delete(key)
        return next
      })
    },
    []
  )

  const handleColorThresholdChange = useCallback(
    (key: string, threshold: GeckoColorScaleThreshold) => {
      setColorThresholds((prev) => new Map(prev).set(key, threshold))
    },
    []
  )

  const handleCategoricalValuesSelected = useCallback(
    (cdfId: string, values: number[] | undefined) => {
      setCategoricalValues((prev) => {
        const next = new Map(prev)
        if (values) next.set(cdfId, values)
        else next.delete(cdfId)
        return next
      })
    },
    []
  )

  const handleCdfSelectionChange = useCallback(
    (componentId: string, cdfId: string) => {
      setSelectedCdfIds((prev) => {
        const next = new Map(prev)
        const defaultCdf = initialComponentDataFrames.find(
          (c) => c.tags.component === componentId
        )
        if (defaultCdf?.id === cdfId) next.delete(componentId)
        else next.set(componentId, cdfId)
        return next
      })
    },
    [initialComponentDataFrames]
  )

  const accordionItems = useMemo(() => {
    return componentDataFrames
      .map((cdf): AccordionItem | null => {
        const componentId = cdf.tags.component
        const component = components.find((c) => c.id === componentId)
        const componentDefaults_ = componentDefaults.find(
          (cd) => cd.tags.component === componentId
        )
        const colorThreshold = getColorThresholdForComponent(cdf)
        const componentCdfs = componentId
          ? cdfsByComponent.get(componentId) || []
          : []

        if (!component) return null

        return {
          key: cdf.id,
          title: component.data.name || componentId,
          content: (
            <Section>
              {componentCdfs.length > 1 && componentId && (
                <>
                  <Label>Inspection Version</Label>
                  <Select
                    value={selectedCdfIds.get(componentId) || cdf.id}
                    onChange={(e) =>
                      handleCdfSelectionChange(componentId, e.target.value)
                    }>
                    {componentCdfs.map((componentCdf) => {
                      const inspectionId = componentCdf.tags.inspection
                      const datasetId = componentCdf.tags['component-dataset']
                      return (
                        <option key={componentCdf.id} value={componentCdf.id}>
                          {inspectionId || datasetId || 'Unknown'} -{' '}
                          {componentCdf.data.params.view.name}
                        </option>
                      )
                    })}
                  </Select>
                </>
              )}
              <Label>
                {cdf.data.params.view.name} - {cdf.data.params.aggregation.name}
              </Label>
              <ComponentFilterInput
                cdf={cdf}
                componentDefaults_={componentDefaults_}
                colorThreshold={colorThreshold}
                filteredValueRange={getFilteredValueRange(cdf)}
                onColorThresholdChange={(threshold) =>
                  handleColorThresholdChange(
                    `${cdf.tags.component}:${cdf.data.params.view.id}`,
                    threshold
                  )
                }
                onFilteredValueRangeChange={(range) =>
                  handleFilteredValueRangeChange(
                    `${cdf.tags.component}:${cdf.data.params.view.id}`,
                    range
                  )
                }
                onCategoricalValuesSelected={(values) =>
                  handleCategoricalValuesSelected(cdf.id, values)
                }
              />
            </Section>
          ),
        }
      })
      .filter((item): item is AccordionItem => item !== null)
  }, [
    componentDataFrames,
    components,
    componentDefaults,
    selectedCdfIds,
    cdfsByComponent,
    getColorThresholdForComponent,
    getFilteredValueRange,
    handleColorThresholdChange,
    handleFilteredValueRangeChange,
    handleCategoricalValuesSelected,
    handleCdfSelectionChange,
  ])

  return (
    <SlashCommandModal onClick={onClose}>
      <ModalContentWrapper onClick={(e) => e.stopPropagation()}>
        <SlashCommandModalHeader>
          Configure View Settings - {entryName}
        </SlashCommandModalHeader>
        <ModalBodyWrapper>
          {components.length > 0 &&
          (isSingleComponentView || (isMCVConfigured && assetModel)) ? (
            <ContentLayout>
              <VizContainer>
                <ForecastAssetViz
                  assetModel={assetModel}
                  components={components}
                  componentDataFrames={componentDataFrames}
                  showMultiComponentView={!isSingleComponentView}
                  markupLayers={[]}
                  getColorThresholdForComponent={getColorThresholdForComponent}
                  cdfToFilteredValueRange={getFilteredValueRange}
                  cdfToSelectedCategoricalValues={getSelectedCategoricalValues}
                  sceneConfig={DEFAULT_SCENE_CONFIG}
                  mode={
                    isSingleComponentView ? AssetVizMode.SelectBin : undefined
                  }
                />
              </VizContainer>
              <ControlsPanel>
                {accordionItems.length === 0 ? (
                  <Message>No components found for this unit</Message>
                ) : (
                  <Accordion
                    backgroundColor={theme.forecast.cardBackground}
                    defaultValue={
                      accordionItems.length === 1 ? [accordionItems[0].key] : []
                    }
                    items={accordionItems}
                  />
                )}
              </ControlsPanel>
            </ContentLayout>
          ) : (
            <Message>
              No multi-component view configured for this unit. Please configure
              the asset model to enable visualization controls.
            </Message>
          )}
        </ModalBodyWrapper>
        <ModalFooter>
          <ButtonGroup>
            <CantileverButton onClick={handleResetChanges}>
              Reset to Defaults
            </CantileverButton>
            <CantileverButton
              primary
              onClick={handleSaveChanges}
              title='Save current settings'>
              Save Changes
            </CantileverButton>
          </ButtonGroup>
          <CantileverButton primary onClick={onClose}>
            Close
          </CantileverButton>
        </ModalFooter>
      </ModalContentWrapper>
    </SlashCommandModal>
  )
}
